import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useUserAuth } from "../../context/userAuthContext";
import { db, storage } from "../../firebase";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
    Image as ImageIcon,
    Code,
    Link as LinkIcon,
    Save,
    ArrowLeft,
    Plus,
    Trash2,
    Clock,
    AlertCircle,
    X,
} from "lucide-react";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { coldarkDark } from "react-syntax-highlighter/dist/esm/styles/prism";

// Colores del logo de Fronios
const logoColors = {
    blue: "#0088D1", // Azul
    teal: "#33B8B8", // Verde-azulado
    orange: "#F08234", // Naranja
    green: "#6CC24A", // Verde
    darkBlue: "#0F3246", // Azul oscuro del centro
};

// Traducciones para los textos
const translations = {
    en: {
        createArticle: "Create Article",
        title: "Title",
        titlePlaceholder: "Enter article title...",
        summary: "Summary",
        summaryPlaceholder: "Enter a brief summary of the article...",
        coverImage: "Cover Image",
        coverImageDescription: "The main image that will be displayed with your article",
        uploadImage: "Upload Image",
        content: "Content",
        tags: "Tags",
        tagsPlaceholder: "Add a tag...",
        addTag: "Add",
        publish: "Publish",
        draft: "Save as Draft",
        back: "Back",
        uploadProgress: "Upload progress",
        uploadComplete: "Upload complete!",
        addCode: "Add Code Snippet",
        language: "Language",
        code: "Code",
        codePlaceholder: "Paste your code here...",
        add: "Add",
        cancel: "Cancel",
        insertLink: "Insert Link",
        urlPlaceholder: "https://example.com",
        textPlaceholder: "Link text",
        saving: "Saving...",
        saved: "Saved!",
        error: "An error occurred",
        publishConfirm: "Your article will be published. Continue?",
        categoryLabel: "Category",
        categories: [
            "Technology",
            "AI",
            "Cloud Computing",
            "3D Printing",
            "Business",
            "Innovation",
            "Development",
            "Case Studies",
        ],
        selectCategory: "Select a category",
        loading: "Loading..."
    },
    es: {
        createArticle: "Crear Artículo",
        title: "Título",
        titlePlaceholder: "Ingrese el título del artículo...",
        summary: "Resumen",
        summaryPlaceholder: "Ingrese un breve resumen del artículo...",
        coverImage: "Imagen de Portada",
        coverImageDescription: "La imagen principal que se mostrará con su artículo",
        uploadImage: "Subir Imagen",
        content: "Contenido",
        tags: "Etiquetas",
        tagsPlaceholder: "Agregar una etiqueta...",
        addTag: "Agregar",
        publish: "Publicar",
        draft: "Guardar como Borrador",
        back: "Volver",
        uploadProgress: "Progreso de carga",
        uploadComplete: "¡Carga completada!",
        addCode: "Agregar Fragmento de Código",
        language: "Lenguaje",
        code: "Código",
        codePlaceholder: "Pegue su código aquí...",
        add: "Agregar",
        cancel: "Cancelar",
        insertLink: "Insertar Enlace",
        urlPlaceholder: "https://ejemplo.com",
        textPlaceholder: "Texto del enlace",
        saving: "Guardando...",
        saved: "¡Guardado!",
        error: "Ocurrió un error",
        publishConfirm: "Su artículo será publicado. ¿Continuar?",
        categoryLabel: "Categoría",
        categories: [
            "Tecnología",
            "IA",
            "Computación en la Nube",
            "Impresión 3D",
            "Negocios",
            "Innovación",
            "Desarrollo",
            "Casos de Estudio",
        ],
        selectCategory: "Seleccione una categoría",
        loading: "Cargando..."
    },
};

// Módulos y formatos para ReactQuill
const quillModules = {
    toolbar: [
        [{ header: [1, 2, 3, 4, 5, 6, false] }],
        ["bold", "italic", "underline", "strike", "blockquote"],
        [{ list: "ordered" }, { list: "bullet" }],
        ["clean"],
    ],
};

const quillFormats = [
    "header",
    "bold",
    "italic",
    "underline",
    "strike",
    "blockquote",
    "list",
    "bullet",
    "indent",
    "link",
    "image",
];

const CodeSnippetModal = ({ isOpen, onClose, onSave, t }) => {
    const [language, setLanguage] = useState("javascript");
    const [code, setCode] = useState("");

    const handleSave = () => {
        onSave(language, code);
        setLanguage("javascript");
        setCode("");
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
            <div className="bg-white rounded-lg w-full max-w-2xl p-6 shadow-2xl">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-bold text-gray-800">{t.addCode}</h2>
                    <button
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-700"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t.language}
                    </label>
                    <select
                        value={language}
                        onChange={(e) => setLanguage(e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    >
                        <option value="javascript">JavaScript</option>
                        <option value="python">Python</option>
                        <option value="java">Java</option>
                        <option value="csharp">C#</option>
                        <option value="cpp">C++</option>
                        <option value="php">PHP</option>
                        <option value="ruby">Ruby</option>
                        <option value="swift">Swift</option>
                        <option value="go">Go</option>
                        <option value="typescript">TypeScript</option>
                        <option value="jsx">JSX</option>
                        <option value="tsx">TSX</option>
                        <option value="html">HTML</option>
                        <option value="css">CSS</option>
                        <option value="json">JSON</option>
                        <option value="sql">SQL</option>
                    </select>
                </div>

                <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t.code}
                    </label>
                    <textarea
                        value={code}
                        onChange={(e) => setCode(e.target.value)}
                        placeholder={t.codePlaceholder}
                        rows={10}
                        className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                    ></textarea>
                </div>

                <div className="preview-container mb-6">
                    <p className="text-sm font-medium text-gray-700 mb-1">Preview:</p>
                    <div className="border border-gray-200 rounded-md overflow-hidden">
                        <SyntaxHighlighter language={language} style={coldarkDark}>
                            {code || "// Your code preview will appear here"}
                        </SyntaxHighlighter>
                    </div>
                </div>

                <div className="flex justify-end space-x-3">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                    >
                        {t.cancel}
                    </button>
                    <button
                        onClick={handleSave}
                        className="px-4 py-2 text-white rounded-md"
                        style={{ backgroundColor: logoColors.blue }}
                        disabled={!code.trim()}
                    >
                        {t.add}
                    </button>
                </div>
            </div>
        </div>
    );
};

const LinkModal = ({ isOpen, onClose, onSave, t }) => {
    const [url, setUrl] = useState("");
    const [text, setText] = useState("");

    const handleSave = () => {
        onSave(url, text);
        setUrl("");
        setText("");
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
            <div className="bg-white rounded-lg w-full max-w-md p-6 shadow-2xl">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-bold text-gray-800">{t.insertLink}</h2>
                    <button
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-700"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        URL
                    </label>
                    <input
                        type="url"
                        value={url}
                        onChange={(e) => setUrl(e.target.value)}
                        placeholder={t.urlPlaceholder}
                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    />
                </div>

                <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t.textPlaceholder}
                    </label>
                    <input
                        type="text"
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        placeholder={t.textPlaceholder}
                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    />
                </div>

                <div className="flex justify-end space-x-3">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                    >
                        {t.cancel}
                    </button>
                    <button
                        onClick={handleSave}
                        className="px-4 py-2 text-white rounded-md"
                        style={{ backgroundColor: logoColors.blue }}
                        disabled={!url.trim()}
                    >
                        {t.add}
                    </button>
                </div>
            </div>
        </div>
    );
};

const BlogMaker = () => {
    const navigate = useNavigate();
    const { user, currentLanguage } = useUserAuth();
    const t = translations[currentLanguage || "es"];
    const fileInputRef = useRef(null);

    const [title, setTitle] = useState("");
    const [summary, setSummary] = useState("");
    const [content, setContent] = useState("");
    const [tags, setTags] = useState([]);
    const [tagInput, setTagInput] = useState("");
    const [coverImage, setCoverImage] = useState(null);
    const [coverImageUrl, setCoverImageUrl] = useState("");
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isUploading, setIsUploading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [status, setStatus] = useState(null);
    const [showCodeModal, setShowCodeModal] = useState(false);
    const [showLinkModal, setShowLinkModal] = useState(false);
    const [category, setCategory] = useState("");
    const [isLoading, setIsLoading] = useState(true);

    const quillRef = useRef(null);

    // Verificar si el usuario está autenticado
    useEffect(() => {
        if (!user) {
            navigate("/login");
            return;
        }

        // Ya no verificamos si es admin
        setIsLoading(false);
    }, [user, navigate]);

    // Mostrar pantalla de carga mientras verificamos
    if (isLoading) {
        return (
            <div className="bg-gray-50 min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <Clock className="w-12 h-12 text-blue-500 animate-spin mx-auto mb-4" />
                    <p className="text-gray-600">{t.loading}</p>
                </div>
            </div>
        );
    }

    const handleFileChange = (e) => {
        if (e.target.files[0]) {
            setCoverImage(e.target.files[0]);
            setCoverImageUrl(URL.createObjectURL(e.target.files[0]));
        }
    };

    const handleAddTag = () => {
        if (tagInput && !tags.includes(tagInput)) {
            setTags([...tags, tagInput]);
            setTagInput("");
        }
    };

    const handleRemoveTag = (tag) => {
        setTags(tags.filter((t) => t !== tag));
    };

    const handleInsertImage = () => {
        fileInputRef.current.click();
    };

    const handleUploadImage = async (file) => {
        if (!file) return null;

        setIsUploading(true);
        setUploadProgress(0);

        const storageRef = ref(storage, `blog-images/${user.uid}/${Date.now()}_${file.name}`);
        const uploadTask = uploadBytesResumable(storageRef, file);

        return new Promise((resolve, reject) => {
            uploadTask.on(
                "state_changed",
                (snapshot) => {
                    const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                    setUploadProgress(progress);
                },
                (error) => {
                    setIsUploading(false);
                    reject(error);
                },
                async () => {
                    const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                    setIsUploading(false);
                    setUploadProgress(100);
                    resolve(downloadURL);
                }
            );
        });
    };

    const handleImageUpload = async () => {
        const input = document.createElement("input");
        input.setAttribute("type", "file");
        input.setAttribute("accept", "image/*");
        input.click();

        input.onchange = async () => {
            const file = input.files[0];
            try {
                const imageUrl = await handleUploadImage(file);

                const quill = quillRef.current.getEditor();
                const range = quill.getSelection(true);
                quill.insertEmbed(range.index, "image", imageUrl);
                quill.setSelection(range.index + 1);
            } catch (error) {
                console.error("Error uploading image:", error);
                setStatus({ type: "error", message: t.error });
            }
        };
    };

    const handleAddCodeSnippet = (language, code) => {
        if (!code) return;

        const codeBlock = `
      <div class="code-snippet">
        <div class="code-header">
          <span class="language">${language}</span>
        </div>
        <pre><code class="language-${language}">${code.replace(/</g, "&lt;").replace(/>/g, "&gt;")}</code></pre>
      </div>
    `;

        const quill = quillRef.current.getEditor();
        const range = quill.getSelection(true);
        quill.clipboard.dangerouslyPasteHTML(range.index, codeBlock);
        quill.setSelection(range.index + 1);
    };

    const handleAddLink = (url, text) => {
        if (!url) return;

        const quill = quillRef.current.getEditor();
        const range = quill.getSelection(true);

        if (range) {
            if (range.length > 0) {
                quill.formatText(range.index, range.length, "link", url);
            } else {
                quill.insertText(range.index, text || url, "link", url);
                quill.setSelection(range.index + (text || url).length);
            }
        }
    };

    const handleSubmit = async (isDraft = false) => {
        if (!title || !content) {
            setStatus({ type: "error", message: "Title and content are required" });
            return;
        }

        if (!isDraft && !window.confirm(t.publishConfirm)) {
            return;
        }

        setIsSubmitting(true);
        setStatus({ type: "loading", message: t.saving });

        try {
            let coverImageURL = coverImageUrl;

            if (coverImage && typeof coverImage !== "string") {
                coverImageURL = await handleUploadImage(coverImage);
            }

            // Usar addDoc para generar automáticamente un ID
            const blogCollectionRef = collection(db, "blogs");

            const docRef = await addDoc(blogCollectionRef, {
                title,
                summary,
                content,
                coverImage: coverImageURL,
                tags,
                category,
                authorId: user.uid,
                authorEmail: user.email,
                authorName: user.displayName || user.email.split('@')[0],
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
                published: !isDraft,
                slug: title
                    .toLowerCase()
                    .replace(/[^\w\s]/gi, "")
                    .replace(/\s+/g, "-"),
            });

            setStatus({ type: "success", message: t.saved });

            setTimeout(() => {
                navigate("/blog");
            }, 1500);
        } catch (error) {
            console.error("Error saving blog:", error);
            setStatus({ type: "error", message: error.message });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="bg-gray-50 min-h-screen pt-24 pb-12">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
                <div className="mb-6 flex justify-between items-center">
                    <button
                        onClick={() => navigate(-1)}
                        className="flex items-center text-gray-600 hover:text-gray-900"
                    >
                        <ArrowLeft className="w-5 h-5 mr-1" />
                        {t.back}
                    </button>
                    <h1 className="text-2xl font-bold text-center text-gray-800">
                        {t.createArticle}
                    </h1>
                    <div className="w-20"></div> {/* Spacer for centering */}
                </div>

                <div className="bg-white rounded-lg shadow-md p-6 mb-8">
                    {/* Title Input */}
                    <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t.title}
                        </label>
                        <input
                            type="text"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder={t.titlePlaceholder}
                            className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-lg"
                        />
                    </div>

                    {/* Summary Input */}
                    <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t.summary}
                        </label>
                        <textarea
                            value={summary}
                            onChange={(e) => setSummary(e.target.value)}
                            placeholder={t.summaryPlaceholder}
                            rows={3}
                            className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        ></textarea>
                    </div>

                    {/* Category Selector */}
                    <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t.categoryLabel}
                        </label>
                        <select
                            value={category}
                            onChange={(e) => setCategory(e.target.value)}
                            className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        >
                            <option value="">{t.selectCategory}</option>
                            {t.categories.map((cat, index) => (
                                <option key={index} value={cat}>
                                    {cat}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Cover Image Upload */}
                    <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t.coverImage}
                        </label>
                        <p className="text-xs text-gray-500 mb-2">
                            {t.coverImageDescription}
                        </p>

                        <div
                            className={`border-2 border-dashed rounded-lg p-6 text-center ${coverImageUrl
                                ? "border-green-300 bg-green-50"
                                : "border-gray-300 hover:border-blue-400 bg-gray-50"
                                } transition-colors cursor-pointer`}
                            onClick={() => fileInputRef.current.click()}
                        >
                            {coverImageUrl ? (
                                <div>
                                    <img
                                        src={coverImageUrl}
                                        alt="Cover"
                                        className="mx-auto max-h-60 rounded"
                                    />
                                    <p className="mt-2 text-sm text-green-600">
                                        {t.uploadComplete}
                                    </p>
                                </div>
                            ) : (
                                <div>
                                    <ImageIcon className="w-12 h-12 mx-auto text-gray-400" />
                                    <p className="mt-2 text-sm text-gray-500">{t.uploadImage}</p>
                                </div>
                            )}
                            <input
                                type="file"
                                accept="image/*"
                                onChange={handleFileChange}
                                ref={fileInputRef}
                                className="hidden"
                            />
                        </div>

                        {isUploading && (
                            <div className="mt-2">
                                <p className="text-sm text-gray-600 mb-1">
                                    {t.uploadProgress}: {Math.round(uploadProgress)}%
                                </p>
                                <div className="w-full bg-gray-200 rounded-full h-2">
                                    <div
                                        className="bg-green-500 h-2 rounded-full"
                                        style={{ width: `${uploadProgress}%` }}
                                    ></div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Content Editor */}
                    <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t.content}
                        </label>

                        <div className="editor-toolbar mb-2 flex flex-wrap gap-2">
                            <button
                                type="button"
                                onClick={handleImageUpload}
                                className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md text-sm bg-white hover:bg-gray-50"
                            >
                                <ImageIcon className="w-4 h-4 mr-1" />
                                Image

                            </button>
                            <button
                                type="button"
                                onClick={() => setShowCodeModal(true)}
                                className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md text-sm bg-white hover:bg-gray-50"
                            >
                                <Code className="w-4 h-4 mr-1" />
                                Code
                            </button>

                            <button
                                type="button"
                                onClick={() => setShowLinkModal(true)}
                                className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md text-sm bg-white hover:bg-gray-50"
                            >
                                <LinkIcon className="w-4 h-4 mr-1" />
                                Link
                            </button>
                        </div>

                        <ReactQuill
                            theme="snow"
                            value={content}
                            onChange={setContent}
                            modules={quillModules}
                            formats={quillFormats}
                            className="h-80 mb-12"
                            ref={quillRef}
                        />
                    </div>

                    {/* Tags Input */}
                    <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t.tags}
                        </label>
                        <div className="flex flex-wrap gap-2 mb-2">
                            {tags.map((tag, i) => (
                                <div
                                    key={i}
                                    className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm flex items-center"
                                >
                                    {tag}
                                    <button
                                        onClick={() => handleRemoveTag(tag)}
                                        className="ml-2 focus:outline-none"
                                    >
                                        <X className="w-3 h-3 text-blue-800" />
                                    </button>
                                </div>
                            ))}
                        </div>
                        <div className="flex">
                            <input
                                type="text"
                                value={tagInput}
                                onChange={(e) => setTagInput(e.target.value)}
                                onKeyPress={(e) => e.key === "Enter" && handleAddTag()}
                                placeholder={t.tagsPlaceholder}
                                className="flex-grow p-2 border border-gray-300 rounded-l-md focus:ring-blue-500 focus:border-blue-500"
                            />
                            <button
                                onClick={handleAddTag}
                                disabled={!tagInput}
                                className="px-4 py-2 text-white rounded-r-md"
                                style={{ backgroundColor: logoColors.teal }}
                            >
                                {t.addTag}
                            </button>
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-wrap gap-4 justify-end">
                        <button
                            onClick={() => handleSubmit(true)}
                            disabled={isSubmitting}
                            className="px-4 py-2 text-white flex items-center rounded-md"
                            style={{ backgroundColor: logoColors.darkBlue }}
                        >
                            <Save className="w-4 h-4 mr-2" />
                            {t.draft}
                        </button>

                        <button
                            onClick={() => handleSubmit(false)}
                            disabled={isSubmitting}
                            className="px-4 py-2 text-white flex items-center rounded-md"
                            style={{ backgroundColor: logoColors.blue }}
                        >
                            <Plus className="w-4 h-4 mr-2" />
                            {t.publish}
                        </button>
                    </div>

                    {/* Status Messages */}
                    {status && (
                        <div
                            className={`mt-4 p-3 rounded-md ${status.type === "success"
                                ? "bg-green-50 text-green-700"
                                : status.type === "error"
                                    ? "bg-red-50 text-red-700"
                                    : "bg-blue-50 text-blue-700"
                                }`}
                        >
                            <div className="flex items-center">
                                {status.type === "success" ? (
                                    <Save className="w-5 h-5 mr-2" />
                                ) : status.type === "error" ? (
                                    <AlertCircle className="w-5 h-5 mr-2" />
                                ) : (
                                    <Clock className="w-5 h-5 mr-2 animate-spin" />
                                )}
                                {status.message}
                            </div>
                        </div>
                    )}
                </div>

                {/* Code Snippet Modal */}
                <CodeSnippetModal
                    isOpen={showCodeModal}
                    onClose={() => setShowCodeModal(false)}
                    onSave={handleAddCodeSnippet}
                    t={t}
                />

                {/* Link Modal */}
                <LinkModal
                    isOpen={showLinkModal}
                    onClose={() => setShowLinkModal(false)}
                    onSave={handleAddLink}
                    t={t}
                />
            </div>
        </div>
    );
};

export default BlogMaker;